<template>
  <div>
    <spinner-loader :loadingStuff="loadingStuff" />
    <app-template />
    <div v-if="userData.name !== ''" class="uk-container uk-container-large">
      <!-- Modern Header Section -->
      <div class="documents-header">
        <div class="header-left">
          <h1><i class="bi bi-folder2-open"></i> Document Library</h1>
          <p class="header-subtitle">Manage and organize your club documents</p>
        </div>
        <div class="header-actions">
          <button class="btn btn-success upload-btn" @click="showModal('documentNewEditModal')">
            <i class="bi bi-cloud-arrow-up"></i> Upload Document
          </button>
        </div>
      </div>

      <!-- Search and Filter Bar -->
      <div class="search-filter-bar">
        <div class="search-box">
          <i class="bi bi-search"></i>
          <input type="text" v-model="searchQuery" placeholder="Search documents by name..." @input="filterDocuments" />
          <button v-if="searchQuery" class="clear-search" @click="clearSearch">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
        <div class="filter-controls">
          <select v-model="sortBy" class="sort-select" @change="sortDocuments">
            <option value="date-desc">Newest First</option>
            <option value="date-asc">Oldest First</option>
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
          </select>
        </div>
      </div>

      <!-- File Type Filter Panel -->
      <div class="filter-panel">
        <span class="filter-label"><i class="bi bi-funnel"></i> Filter by type:</span>
        <div class="filter-chips">
          <button
            :class="['filter-chip', { active: fileTypeFilter === 'all' }]"
            @click="fileTypeFilter = 'all'">
            <i class="bi bi-file-earmark"></i> All
          </button>
          <button
            :class="['filter-chip', { active: fileTypeFilter === 'pdf' }]"
            @click="fileTypeFilter = 'pdf'">
            <i class="bi bi-file-earmark-pdf"></i> PDF
          </button>
          <button
            :class="['filter-chip', { active: fileTypeFilter === 'doc' }]"
            @click="fileTypeFilter = 'doc'">
            <i class="bi bi-file-earmark-word"></i> DOC
          </button>
          <button
            :class="['filter-chip', { active: fileTypeFilter === 'image' }]"
            @click="fileTypeFilter = 'image'">
            <i class="bi bi-file-earmark-image"></i> Images
          </button>
        </div>
        <button v-if="hasActiveFilters" class="clear-filters-btn" @click="clearAllFilters">
          <i class="bi bi-x-circle"></i> Clear filters
        </button>
      </div>

      <div class="sessions-week-layout">
        <!-- Modern Tabs -->
        <ul class="document-tabs">
          <li class="tab-item">
            <a :class="selectedTab === 'club-tab' ? 'tab-link active' : 'tab-link'"
              @click="tabChange('selectedTab', 'club-tab')" href="javascript:;">
              <i class="bi bi-building"></i>
              <span>Club</span>
              <span class="tab-badge" v-if="getDocumentCount('club') > 0">{{ getDocumentCount('club') }}</span>
            </a>
          </li>
          <li class="tab-item">
            <a :class="selectedTab === 'team-tab' ? 'tab-link active' : 'tab-link'"
              @click="tabChange('selectedTab', 'team-tab')" href="javascript:;">
              <i class="bi bi-people"></i>
              <span>Teams</span>
              <span class="tab-badge" v-if="getDocumentCount('team') > 0">{{ getDocumentCount('team') }}</span>
            </a>
          </li>
          <li class="tab-item">
            <a :class="selectedTab === 'player-tab' ? 'tab-link active' : 'tab-link'"
              @click="tabChange('selectedTab', 'player-tab')" href="javascript:;">
              <i class="bi bi-person"></i>
              <span>Players</span>
              <span class="tab-badge" v-if="getDocumentCount('player') > 0">{{ getDocumentCount('player') }}</span>
            </a>
          </li>
          <li v-if="userData.userType === 'Admin'" class="tab-item">
            <a :class="selectedTab === 'coach-tab' ? 'tab-link active' : 'tab-link'"
              @click="tabChange('selectedTab', 'coach-tab')" href="javascript:;">
              <i class="bi bi-clipboard-check"></i>
              <span>Coaches</span>
              <span class="tab-badge" v-if="getDocumentCount('coach') > 0">{{ getDocumentCount('coach') }}</span>
            </a>
          </li>
          <li v-if="userData.userType === 'Admin' || userData.userType === 'Scout'" class="tab-item">
            <a :class="selectedTab === 'scout-tab' ? 'tab-link active' : 'tab-link'"
              @click="tabChange('selectedTab', 'scout-tab')" href="javascript:;">
              <i class="bi bi-binoculars"></i>
              <span>Scouts</span>
              <span class="tab-badge" v-if="getDocumentCount('scout') > 0">{{ getDocumentCount('scout') }}</span>
            </a>
          </li>
          <li v-if="userData.userType === 'Admin'" class="tab-item">
            <a :class="selectedTab === 'admin-tab' ? 'tab-link active' : 'tab-link'"
              @click="tabChange('selectedTab', 'admin-tab')" href="javascript:;">
              <i class="bi bi-shield-lock"></i>
              <span>Admin</span>
              <span class="tab-badge" v-if="getDocumentCount('admin') > 0">{{ getDocumentCount('admin') }}</span>
            </a>
          </li>
          <li class="tab-item">
            <a :class="selectedTab === 'physioMain-tab' ? 'tab-link active' : 'tab-link'"
              @click="tabChange('selectedTab', 'physioMain-tab')" href="javascript:;">
              <i class="bi bi-heart-pulse"></i>
              <span>Physio</span>
              <span class="tab-badge" v-if="getDocumentCount('physioMain') > 0">{{ getDocumentCount('physioMain')
                }}</span>
            </a>
          </li>
          <li v-if="userData.userType === 'Admin'" class="tab-item">
            <a :class="selectedTab === 'global-tab' ? 'tab-link active' : 'tab-link'"
              @click="tabChange('selectedTab', 'global-tab')" href="javascript:;">
              <i class="bi bi-globe"></i>
              <span>Global</span>
            </a>
          </li>
        </ul>
        <div class="tabs-wrapper">
          <div :class="gridListView === 'grid'
            ? 'current-week-tab documents-tab tab-content grid-view-wrapper'
            : 'current-week-tab documents-tab tab-content list-view-wrapper'
            " v-if="selectedTab === 'club-tab'">
            <div class="view-controls">
              <span class="results-count">{{ getFilteredDocuments('club').length }} document{{
                getFilteredDocuments('club').length !== 1 ? 's' : '' }}</span>
              <div class="view-toggle">
                <button @click="gridListSwitch('grid')" :class="['view-btn', { active: gridListView === 'grid' }]"
                  title="Grid view"><i class="bi bi-grid-3x3-gap-fill"></i></button>
                <button @click="gridListSwitch('list')" :class="['view-btn', { active: gridListView === 'list' }]"
                  title="List view"><i class="bi bi-list-ul"></i></button>
              </div>
            </div>

            <div :class="gridListView === 'grid' ? 'document-card' : 'document-list-item'"
              v-for="document in getFilteredDocuments('club')" :key="document.id">
              <div class="doc-icon">
                <i class="bi bi-file-earmark-pdf-fill"></i>
              </div>
              <div class="doc-info">
                <h3 class="doc-name">{{ removeNumberFromStart(document.name) }}</h3>
                <div class="doc-meta">
                  <span class="meta-item"><i class="bi bi-calendar3"></i> {{ formatDate(document.created_date) }}</span>
                  <span class="meta-item category-badge">{{ document.category }}</span>
                </div>
              </div>
              <div class="doc-actions">
                <button class="action-btn view-btn-action" @click="openDocument(document)" title="View document">
                  <i class="bi bi-eye"></i>
                </button>
                <a :href="getDocumentUrl(document.url)" :download="removeNumberFromStart(document.name)"
                  class="action-btn download-btn" title="Download">
                  <i class="bi bi-download"></i>
                </a>
                <button class="action-btn delete-btn" @click="removeUploadedDocument(document.id, document.name)"
                  title="Delete">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>

            <div v-if="getFilteredDocuments('club').length === 0" class="empty-state-modern">
              <div class="empty-icon">
                <i class="bi bi-folder2-open"></i>
              </div>
              <h3>No club documents</h3>
              <p>Upload your first club document to get started</p>
              <button class="btn btn-outline-success" @click="showModal('documentNewEditModal')">
                <i class="bi bi-cloud-arrow-up"></i> Upload Document
              </button>
            </div>
          </div>

          <div :class="gridListView === 'grid'
            ? 'current-week-tab documents-tab tab-content grid-view-wrapper'
            : 'current-week-tab documents-tab tab-content list-view-wrapper'
            " v-if="selectedTab === 'team-tab'">
            <div class="view-controls">
              <span class="results-count">{{ getFilteredDocuments('team').length }} document{{
                getFilteredDocuments('team').length !== 1 ? 's' : '' }}</span>
              <div class="view-toggle">
                <button @click="gridListSwitch('grid')" :class="['view-btn', { active: gridListView === 'grid' }]"
                  title="Grid view"><i class="bi bi-grid-3x3-gap-fill"></i></button>
                <button @click="gridListSwitch('list')" :class="['view-btn', { active: gridListView === 'list' }]"
                  title="List view"><i class="bi bi-list-ul"></i></button>
              </div>
            </div>

            <div :class="gridListView === 'grid' ? 'document-card' : 'document-list-item'"
              v-for="document in getFilteredDocuments('team')" :key="document.id">
              <div class="doc-icon"><i class="bi bi-file-earmark-pdf-fill"></i></div>
              <div class="doc-info">
                <h3 class="doc-name">{{ removeNumberFromStart(document.name) }}</h3>
                <div class="doc-meta">
                  <span class="meta-item"><i class="bi bi-calendar3"></i> {{ formatDate(document.created_date) }}</span>
                  <span class="meta-item category-badge">{{ document.category }}</span>
                </div>
              </div>
              <div class="doc-actions">
                <button class="action-btn view-btn-action" @click="openDocument(document)" title="View"><i
                    class="bi bi-eye"></i></button>
                <a :href="getDocumentUrl(document.url)" :download="removeNumberFromStart(document.name)"
                  class="action-btn download-btn" title="Download"><i class="bi bi-download"></i></a>
                <button class="action-btn delete-btn" @click="removeUploadedDocument(document.id, document.name)"
                  title="Delete"><i class="bi bi-trash"></i></button>
              </div>
            </div>

            <div v-if="getFilteredDocuments('team').length === 0" class="empty-state-modern">
              <div class="empty-icon"><i class="bi bi-people"></i></div>
              <h3>No team documents</h3>
              <p>Upload documents for your teams</p>
              <button class="btn btn-outline-success" @click="showModal('documentNewEditModal')"><i
                  class="bi bi-cloud-arrow-up"></i> Upload Document</button>
            </div>
          </div>

          <div :class="gridListView === 'grid'
            ? 'current-week-tab documents-tab tab-content grid-view-wrapper'
            : 'current-week-tab documents-tab tab-content list-view-wrapper'
            " v-if="selectedTab === 'coach-tab'">
            <div class="view-controls">
              <span class="results-count">{{ getFilteredDocuments('coach').length }} document{{
                getFilteredDocuments('coach').length !== 1 ? 's' : '' }}</span>
              <div class="view-toggle">
                <button @click="gridListSwitch('grid')" :class="['view-btn', { active: gridListView === 'grid' }]"
                  title="Grid view"><i class="bi bi-grid-3x3-gap-fill"></i></button>
                <button @click="gridListSwitch('list')" :class="['view-btn', { active: gridListView === 'list' }]"
                  title="List view"><i class="bi bi-list-ul"></i></button>
              </div>
            </div>

            <div :class="gridListView === 'grid' ? 'document-card' : 'document-list-item'"
              v-for="document in getFilteredDocuments('coach')" :key="document.id">
              <div class="doc-icon"><i class="bi bi-file-earmark-pdf-fill"></i></div>
              <div class="doc-info">
                <h3 class="doc-name">{{ removeNumberFromStart(document.name) }}</h3>
                <div class="doc-meta">
                  <span class="meta-item"><i class="bi bi-calendar3"></i> {{ formatDate(document.created_date) }}</span>
                  <span class="meta-item category-badge">{{ document.category }}</span>
                </div>
              </div>
              <div class="doc-actions">
                <button class="action-btn view-btn-action" @click="openDocument(document)" title="View"><i
                    class="bi bi-eye"></i></button>
                <a :href="getDocumentUrl(document.url)" :download="removeNumberFromStart(document.name)"
                  class="action-btn download-btn" title="Download"><i class="bi bi-download"></i></a>
                <button class="action-btn delete-btn" @click="removeUploadedDocument(document.id, document.name)"
                  title="Delete"><i class="bi bi-trash"></i></button>
              </div>
            </div>

            <div v-if="getFilteredDocuments('coach').length === 0" class="empty-state-modern">
              <div class="empty-icon"><i class="bi bi-clipboard-check"></i></div>
              <h3>No coach documents</h3>
              <p>Upload documents for coaches</p>
              <button class="btn btn-outline-success" @click="showModal('documentNewEditModal')"><i
                  class="bi bi-cloud-arrow-up"></i> Upload Document</button>
            </div>
          </div>

          <div :class="gridListView === 'grid'
            ? 'current-week-tab documents-tab tab-content grid-view-wrapper'
            : 'current-week-tab documents-tab tab-content list-view-wrapper'
            " v-if="selectedTab === 'scout-tab'">
            <div class="view-controls">
              <span class="results-count">{{ getFilteredDocuments('scout').length }} document{{
                getFilteredDocuments('scout').length !== 1 ? 's' : '' }}</span>
              <div class="view-toggle">
                <button @click="gridListSwitch('grid')" :class="['view-btn', { active: gridListView === 'grid' }]"
                  title="Grid view"><i class="bi bi-grid-3x3-gap-fill"></i></button>
                <button @click="gridListSwitch('list')" :class="['view-btn', { active: gridListView === 'list' }]"
                  title="List view"><i class="bi bi-list-ul"></i></button>
              </div>
            </div>

            <div :class="gridListView === 'grid' ? 'document-card' : 'document-list-item'"
              v-for="document in getFilteredDocuments('scout')" :key="document.id">
              <div class="doc-icon"><i class="bi bi-file-earmark-pdf-fill"></i></div>
              <div class="doc-info">
                <h3 class="doc-name">{{ removeNumberFromStart(document.name) }}</h3>
                <div class="doc-meta">
                  <span class="meta-item"><i class="bi bi-calendar3"></i> {{ formatDate(document.created_date) }}</span>
                  <span class="meta-item category-badge">{{ document.category }}</span>
                </div>
              </div>
              <div class="doc-actions">
                <button class="action-btn view-btn-action" @click="openDocument(document)" title="View"><i
                    class="bi bi-eye"></i></button>
                <a :href="getDocumentUrl(document.url)" :download="removeNumberFromStart(document.name)"
                  class="action-btn download-btn" title="Download"><i class="bi bi-download"></i></a>
                <button class="action-btn delete-btn" @click="removeUploadedDocument(document.id, document.name)"
                  title="Delete"><i class="bi bi-trash"></i></button>
              </div>
            </div>

            <div v-if="getFilteredDocuments('scout').length === 0" class="empty-state-modern">
              <div class="empty-icon"><i class="bi bi-binoculars"></i></div>
              <h3>No scout documents</h3>
              <p>Upload documents for scouts</p>
              <button class="btn btn-outline-success" @click="showModal('documentNewEditModal')"><i
                  class="bi bi-cloud-arrow-up"></i> Upload Document</button>
            </div>
          </div>

          <div :class="gridListView === 'grid'
            ? 'current-week-tab documents-tab tab-content grid-view-wrapper'
            : 'current-week-tab documents-tab tab-content list-view-wrapper'
            " v-if="selectedTab === 'player-tab'">
            <div class="view-controls">
              <span class="results-count">{{ getFilteredDocuments('player').length }} document{{
                getFilteredDocuments('player').length !== 1 ? 's' : '' }}</span>
              <div class="view-toggle">
                <button @click="gridListSwitch('grid')" :class="['view-btn', { active: gridListView === 'grid' }]"
                  title="Grid view"><i class="bi bi-grid-3x3-gap-fill"></i></button>
                <button @click="gridListSwitch('list')" :class="['view-btn', { active: gridListView === 'list' }]"
                  title="List view"><i class="bi bi-list-ul"></i></button>
              </div>
            </div>

            <div :class="gridListView === 'grid' ? 'document-card' : 'document-list-item'"
              v-for="document in getFilteredDocuments('player')" :key="document.id">
              <div class="doc-icon"><i class="bi bi-file-earmark-pdf-fill"></i></div>
              <div class="doc-info">
                <h3 class="doc-name">{{ removeNumberFromStart(document.name) }}</h3>
                <div class="doc-meta">
                  <span class="meta-item"><i class="bi bi-calendar3"></i> {{ formatDate(document.created_date) }}</span>
                  <span class="meta-item category-badge">{{ document.category }}</span>
                </div>
              </div>
              <div class="doc-actions">
                <button class="action-btn view-btn-action" @click="openDocument(document)" title="View"><i
                    class="bi bi-eye"></i></button>
                <a :href="getDocumentUrl(document.url)" :download="removeNumberFromStart(document.name)"
                  class="action-btn download-btn" title="Download"><i class="bi bi-download"></i></a>
                <button class="action-btn delete-btn" @click="removeUploadedDocument(document.id, document.name)"
                  title="Delete"><i class="bi bi-trash"></i></button>
              </div>
            </div>

            <div v-if="getFilteredDocuments('player').length === 0" class="empty-state-modern">
              <div class="empty-icon"><i class="bi bi-person"></i></div>
              <h3>No player documents</h3>
              <p>Upload documents for players</p>
              <button class="btn btn-outline-success" @click="showModal('documentNewEditModal')"><i
                  class="bi bi-cloud-arrow-up"></i> Upload Document</button>
            </div>
          </div>
          <div :class="gridListView === 'grid'
            ? 'current-week-tab documents-tab tab-content grid-view-wrapper'
            : 'current-week-tab documents-tab tab-content list-view-wrapper'
            " v-if="selectedTab === 'admin-tab'">
            <div class="view-controls">
              <span class="results-count">{{ getFilteredDocuments('admin').length }} document{{
                getFilteredDocuments('admin').length !== 1 ? 's' : '' }}</span>
              <div class="view-toggle">
                <button @click="gridListSwitch('grid')" :class="['view-btn', { active: gridListView === 'grid' }]"
                  title="Grid view"><i class="bi bi-grid-3x3-gap-fill"></i></button>
                <button @click="gridListSwitch('list')" :class="['view-btn', { active: gridListView === 'list' }]"
                  title="List view"><i class="bi bi-list-ul"></i></button>
              </div>
            </div>

            <div :class="gridListView === 'grid' ? 'document-card' : 'document-list-item'"
              v-for="document in getFilteredDocuments('admin')" :key="document.id">
              <div class="doc-icon"><i class="bi bi-file-earmark-pdf-fill"></i></div>
              <div class="doc-info">
                <h3 class="doc-name">{{ removeNumberFromStart(document.name) }}</h3>
                <div class="doc-meta">
                  <span class="meta-item"><i class="bi bi-calendar3"></i> {{ formatDate(document.created_date) }}</span>
                  <span class="meta-item category-badge">{{ document.category }}</span>
                </div>
              </div>
              <div class="doc-actions">
                <button class="action-btn view-btn-action" @click="openDocument(document)" title="View"><i
                    class="bi bi-eye"></i></button>
                <a :href="getDocumentUrl(document.url)" :download="removeNumberFromStart(document.name)"
                  class="action-btn download-btn" title="Download"><i class="bi bi-download"></i></a>
                <button class="action-btn delete-btn" @click="removeUploadedDocument(document.id, document.name)"
                  title="Delete"><i class="bi bi-trash"></i></button>
              </div>
            </div>

            <div v-if="getFilteredDocuments('admin').length === 0" class="empty-state-modern">
              <div class="empty-icon"><i class="bi bi-shield-lock"></i></div>
              <h3>No admin documents</h3>
              <p>Upload confidential admin documents</p>
              <button class="btn btn-outline-success" @click="showModal('documentNewEditModal')"><i
                  class="bi bi-cloud-arrow-up"></i> Upload Document</button>
            </div>
          </div>

          <div :class="gridListView === 'grid'
            ? 'current-week-tab documents-tab tab-content grid-view-wrapper'
            : 'current-week-tab documents-tab tab-content list-view-wrapper'
            " v-if="selectedTab === 'global-tab'">
            <div class="doc-library" v-if="userData.userType === 'Admin' || userData.userType === 'Coach'">
              <div class="doc-section">
                <div class="doc-section__head">
                  <h4><i class="bi bi-pen"></i> Signed documents &amp; agreements</h4>
                  <p>Templates your members read and sign — edit the wording, or check who has signed.</p>
                </div>
                <div class="doc-tiles">
                  <div class="doc-tile" v-for="doc in signedDocuments" :key="doc.title">
                    <div class="doc-tile__icon" :class="doc.tone">
                      <i :class="doc.icon"></i>
                    </div>
                    <div class="doc-tile__body">
                      <h5>{{ doc.title }}</h5>
                      <span class="doc-tile__meta">{{ doc.audience }}</span>
                    </div>
                    <div class="doc-tile__actions">
                      <button class="doc-btn doc-btn--edit" @click="openGlobalDoc(doc)">
                        <i class="bi bi-pencil"></i> Edit
                      </button>
                      <button v-if="doc.hasStatus" class="doc-btn doc-btn--ghost" @click="openCharterStatus()">
                        <i class="bi bi-people"></i> Status
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="doc-section">
                <div class="doc-section__head">
                  <h4><i class="bi bi-clipboard-data"></i> Forms</h4>
                  <p>Assessment and development forms.</p>
                </div>
                <div class="doc-tiles">
                  <div class="doc-tile" v-for="doc in formDocuments" :key="doc.title">
                    <div class="doc-tile__icon" :class="doc.tone">
                      <i :class="doc.icon"></i>
                    </div>
                    <div class="doc-tile__body">
                      <h5>{{ doc.title }}</h5>
                      <span class="doc-tile__meta">{{ doc.audience }}</span>
                    </div>
                    <div class="doc-tile__actions">
                      <button class="doc-btn doc-btn--edit" @click="openGlobalDoc(doc)">
                        <i class="bi bi-pencil"></i> Open
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div :class="gridListView === 'grid'
            ? 'current-week-tab documents-tab tab-content grid-view-wrapper'
            : 'current-week-tab documents-tab tab-content list-view-wrapper'
            " v-if="selectedTab === 'physioMain-tab'">
            <div class="view-controls">
              <span class="results-count">{{ getFilteredDocuments('physioMain').length }} document{{
                getFilteredDocuments('physioMain').length !== 1 ? 's' : '' }}</span>
              <div class="view-toggle">
                <button @click="gridListSwitch('grid')" :class="['view-btn', { active: gridListView === 'grid' }]"
                  title="Grid view"><i class="bi bi-grid-3x3-gap-fill"></i></button>
                <button @click="gridListSwitch('list')" :class="['view-btn', { active: gridListView === 'list' }]"
                  title="List view"><i class="bi bi-list-ul"></i></button>
              </div>
            </div>

            <div :class="gridListView === 'grid' ? 'document-card' : 'document-list-item'"
              v-for="document in getFilteredDocuments('physioMain')" :key="document.id">
              <div class="doc-icon"><i class="bi bi-file-earmark-pdf-fill"></i></div>
              <div class="doc-info">
                <h3 class="doc-name">{{ removeNumberFromStart(document.name) }}</h3>
                <div class="doc-meta">
                  <span class="meta-item"><i class="bi bi-calendar3"></i> {{ formatDate(document.created_date) }}</span>
                  <span class="meta-item category-badge">{{ document.category }}</span>
                </div>
              </div>
              <div class="doc-actions">
                <button class="action-btn view-btn-action" @click="openDocument(document)" title="View"><i
                    class="bi bi-eye"></i></button>
                <a :href="getDocumentUrl(document.url)" :download="removeNumberFromStart(document.name)"
                  class="action-btn download-btn" title="Download"><i class="bi bi-download"></i></a>
                <button class="action-btn delete-btn" @click="removeUploadedDocument(document.id, document.name)"
                  title="Delete"><i class="bi bi-trash"></i></button>
              </div>
            </div>

            <div v-if="getFilteredDocuments('physioMain').length === 0" class="empty-state-modern">
              <div class="empty-icon"><i class="bi bi-heart-pulse"></i></div>
              <h3>No physio documents</h3>
              <p>Upload medical and physiotherapy documents</p>
              <button class="btn btn-outline-success" @click="showModal('documentNewEditModal')"><i
                  class="bi bi-cloud-arrow-up"></i> Upload Document</button>
            </div>
          </div>
        </div>
      </div>
      <div></div>
    </div>

    <b-modal id="documentModal" size="xl" :hide-footer="Boolean(true)">
      <div class="performance-target-modal-content">
        <h3>New document</h3>

        <form class="form">
          <div class="form__item">
            <label class="form__label" for="title">Title</label>
            <input class="form__input" type="text" id="title" v-model="documentData.name" />
          </div>

          <QuillEditor v-model:content="documentData.text" contentType="html" />

          <b-form-checkbox id="checkbox-signature" v-model="documentData.signature" name="checkbox-signature">
            Signature required
          </b-form-checkbox>

          <h4>Document access</h4>

          <div class="form__item">
            <label class="form__label" for="select_season">Teams</label>
            <div class="select-user">
              <b-input-group size="sm" class="mb-2 autocomplete">
                <i class="bi bi-search"></i>
                <b-form-input type="search" placeholder="Search teams" @update:modelValue="
                  lookUpSearch($event, 'filterdTeamsData', 'clubTeams')
                  "></b-form-input>
                <ul class="autocomplete-results" v-if="filterdTeamsData.length > 0">
                  <li v-for="teamData in filterdTeamsData" :key="teamData.id" class="autocomplete-result">
                    <span @click="
                      addUserTeamToApprovedAccessList(
                        'Teams',
                        teamData,
                        'documentData',
                      )
                      ">
                      {{ teamData.teamName }}</span>
                  </li>
                </ul>
              </b-input-group>
            </div>
          </div>

          <div class="form__item">
            <label class="form__label" for="select_season">Players</label>

            <div class="select-user">
              <b-input-group size="sm" class="mb-2 autocomplete">
                <i class="bi bi-search"></i>
                <b-form-input type="search" placeholder="Search players" @update:modelValue="
                  lookUpSearch(
                    $event,
                    'filterdCoachesUsersData',
                    'coachesUsersData',
                  )
                  "></b-form-input>
                <ul class="autocomplete-results" v-if="filterdCoachesUsersData.length > 0">
                  <li v-for="userData in filterdCoachesUsersData" :key="userData.id" class="autocomplete-result">
                    <span @click="
                      addUserTeamToApprovedAccessList(
                        'Users',
                        userData,
                        'documentData',
                      )
                      ">
                      {{ userData.name }}</span>
                  </li>
                </ul>
              </b-input-group>
            </div>
          </div>

          <div class="form__item">
            <label class="form__label" for="select_season">Access</label>
            <div class="access-pool">
              <p v-for="accessData in documentData.approvedAccessList.users" :key="accessData.id" class="has-access"
                @click="removeAccess(accessData.id, 'Users')">
                {{ accessData.name }} <span>x</span>
              </p>
              <p v-for="accessData in documentData.approvedAccessList.teams" :key="accessData.id" class="has-access"
                @click="removeAccess(accessData.id, 'Teams')">
                {{ accessData.teamName }} <span>x</span>
              </p>
            </div>
          </div>
        </form>

        <div class="buttons-row">
          <b-button size="lg" variant="outline-success" @click="saveNewDocument()">
            Save</b-button>
        </div>
      </div>
    </b-modal>

    <!-- Enhanced PDF Viewer Modal -->
    <div v-if="showDocumentViewModal && selectedDocumentData && selectedDocumentData.name" class="document-viewer-modal-overlay" @click.self="hideModal('documentViewModal')">
      <div class="document-viewer-modal" :class="{ 'is-fullscreen': isFullscreen }">
        <!-- Modal Header -->
        <div class="viewer-header">
          <div class="viewer-title">
            <i class="bi bi-file-earmark-pdf-fill pdf-icon"></i>
            <h3>{{ removeNumberFromStart(selectedDocumentData.name) }}</h3>
          </div>
          <div class="viewer-controls">
            <button class="control-btn" @click="toggleFullscreen" :title="isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'">
              <i :class="isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-arrows-fullscreen'"></i>
            </button>
            <a v-if="selectedDocumentData.url" :href="getDocumentUrl(selectedDocumentData.url)" target="_blank" class="control-btn" title="Open in New Tab">
              <i class="bi bi-box-arrow-up-right"></i>
            </a>
            <a v-if="selectedDocumentData.url" :href="getDocumentUrl(selectedDocumentData.url)" :download="removeNumberFromStart(selectedDocumentData.name)" class="control-btn download" title="Download">
              <i class="bi bi-download"></i>
            </a>
            <button class="control-btn close" @click="hideModal('documentViewModal')" title="Close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <!-- PDF Viewer Content -->
        <div class="viewer-content">
          <!-- PDF Loading State -->
          <div v-if="pdfLoading" class="pdf-loading">
            <div class="loading-spinner"></div>
            <p>Loading document...</p>
          </div>

          <!-- PDF Viewer -->
          <div v-if="selectedDocumentData.url && isPdfFile(selectedDocumentData.url)" class="pdf-viewer-wrapper">
            <object
              :data="getDocumentUrl(selectedDocumentData.url)"
              type="application/pdf"
              class="pdf-embed"
              @load="onPdfLoad"
              @error="onPdfError">
              <iframe
                :src="getDocumentUrl(selectedDocumentData.url)"
                class="pdf-embed"
                frameborder="0"
                @load="onPdfLoad">
              </iframe>
            </object>
          </div>

          <!-- Image Preview -->
          <div v-else-if="selectedDocumentData.url && isImageFile(selectedDocumentData.url)" class="image-viewer-wrapper">
            <img :src="getDocumentUrl(selectedDocumentData.url)" :alt="removeNumberFromStart(selectedDocumentData.name)" class="image-preview" />
          </div>

          <!-- HTML Content (for non-PDF documents) -->
          <div v-else-if="selectedDocumentData.text" class="html-content" v-html="selectedDocumentData.text"></div>

          <!-- Fallback for other file types -->
          <div v-else-if="selectedDocumentData.url" class="file-fallback">
            <div class="fallback-icon">
              <i class="bi bi-file-earmark"></i>
            </div>
            <h4>Preview not available</h4>
            <p>This file type cannot be previewed in the browser.</p>
            <a :href="getDocumentUrl(selectedDocumentData.url)" :download="removeNumberFromStart(selectedDocumentData.name)" class="download-btn">
              <i class="bi bi-download"></i> Download File
            </a>
          </div>
        </div>

        <!-- Mobile Bottom Bar -->
        <div class="viewer-bottom-bar">
          <a v-if="selectedDocumentData.url" :href="getDocumentUrl(selectedDocumentData.url)" target="_blank" class="action-btn">
            <i class="bi bi-box-arrow-up-right"></i>
            <span>Open</span>
          </a>
          <a v-if="selectedDocumentData.url" :href="getDocumentUrl(selectedDocumentData.url)" :download="removeNumberFromStart(selectedDocumentData.name)" class="action-btn primary">
            <i class="bi bi-download"></i>
            <span>Download</span>
          </a>
        </div>
      </div>
    </div>

    <b-modal id="documentEditModal" size="xl" :hide-footer="Boolean(true)">
      <div class="performance-target-modal-content">
        <h3>Edit document</h3>

        <form class="form">
          <div class="form__item">
            <label class="form__label" for="title">Title</label>
            <input class="form__input" type="text" id="title" v-model="selectedDocumentData.name" />
          </div>

          <QuillEditor v-model:content="selectedDocumentData.text" contentType="html" />

          <b-form-checkbox id="checkbox-signature" v-model="selectedDocumentData.signature" name="checkbox-signature">
            Signature required
          </b-form-checkbox>

          <h4>Document access</h4>

          <div class="form__item">
            <label class="form__label" for="select_season">Team</label>
            <div class="select-user">
              <b-input-group size="sm" class="mb-2 autocomplete">
                <i class="bi bi-search"></i>
                <b-form-input type="search" placeholder="Search players / coaches" @update:modelValue="
                  lookUpSearch($event, 'filterdTeamsData', 'clubTeams')
                  "></b-form-input>
                <ul class="autocomplete-results" v-if="filterdTeamsData.length > 0">
                  <li v-for="teamData in filterdTeamsData" :key="teamData.id" class="autocomplete-result">
                    <span @click="
                      addUserTeamToApprovedAccessList(
                        'Teams',
                        teamData,
                        'selectedDocumentData',
                      )
                      ">
                      {{ teamData.teamName }}</span>
                  </li>
                </ul>
              </b-input-group>
            </div>
          </div>

          <div class="form__item">
            <label class="form__label" for="select_season">Player / Coach</label>

            <div class="select-user">
              <b-input-group size="sm" class="mb-2 autocomplete">
                <i class="bi bi-search"></i>
                <b-form-input type="search" placeholder="Search players / coaches" @input="
                  lookUpSearch(
                    $event,
                    'filterdCoachesUsersData',
                    'coachesUsersData',
                  )
                  "></b-form-input>
                <ul class="autocomplete-results" v-if="filterdCoachesUsersData.length > 0">
                  <li v-for="userData in filterdCoachesUsersData" :key="userData.id" class="autocomplete-result">
                    <span @click="
                      addUserTeamToApprovedAccessList(
                        'Users',
                        userData,
                        'selectedDocumentData',
                      )
                      ">
                      {{ userData.name }}</span>
                  </li>
                </ul>
              </b-input-group>
            </div>
          </div>

          <div class="form__item">
            <label class="form__label" for="select_season">Access</label>
            <div class="access-pool">
              <p v-for="accessData in selectedDocumentData.approvedAccessList
                .users" :key="accessData.id" class="has-access" @click="removeAccess(accessData.id, 'Users')">
                {{ accessData.name }} <span>x</span>
              </p>
              <p v-for="accessData in selectedDocumentData.approvedAccessList
                .teams" :key="accessData.id" class="has-access" @click="removeAccess(accessData.id, 'Teams')">
                {{ accessData.teamName }} <span>x</span>
              </p>
            </div>
          </div>
        </form>

        <div class="buttons-row">
          <b-button size="lg" variant="outline-success" @click="saveEditedDocument(selectedDocumentData)">
            Save</b-button>
        </div>
      </div>
    </b-modal>

    <b-modal v-if="showDocumentNewEditModal" v-model="showDocumentNewEditModal" title="Document" size="xl"
      :hide-footer="Boolean(true)">
      <div class="performance-target-modal-content">
        <form class="form">
          <h4>Document access</h4>

          <div class="form__item">
            <label class="form__label" for="select_season">Category</label>

            <b-form-select v-model="selectedDocumentData.approvedAccessList.category" class="mb-3">
              <b-form-select-option :value="null">Please select an option</b-form-select-option>
              <b-form-select-option value="club">Club document</b-form-select-option>
              <b-form-select-option value="team">Team document</b-form-select-option>
              <b-form-select-option value="player">Player document</b-form-select-option>
              <b-form-select-option value="coach">Coach document</b-form-select-option>
              <b-form-select-option value="scout">Scout document</b-form-select-option>
              <b-form-select-option value="admin">Admin document</b-form-select-option>
              <b-form-select-option value="physioMain">Physio document</b-form-select-option>
            </b-form-select>
          </div>

          <div class="form__item" v-if="selectedDocumentData.approvedAccessList.category === 'team'">
            <label class="form__label" for="select_season">Team</label>
            <div class="select-user">
              <b-input-group size="sm" class="mb-2 autocomplete">
                <i class="bi bi-search"></i>
                <b-form-input type="search" placeholder="Search players / coaches" @update:modelValue="
                  lookUpSearch($event, 'filterdTeamsData', 'clubTeams')
                  "></b-form-input>
                <ul class="autocomplete-results" v-if="filterdTeamsData.length > 0">
                  <li v-for="teamData in filterdTeamsData" :key="teamData.id" class="autocomplete-result">
                    <span @click="
                      addUserTeamToApprovedAccessList(
                        'Teams',
                        teamData,
                        'selectedDocumentData',
                      )
                      ">
                      {{ teamData.teamName }}</span>
                  </li>
                </ul>
              </b-input-group>
            </div>
          </div>

          <div class="form__item" v-if="selectedDocumentData.approvedAccessList.category === 'player'">
            <label class="form__label" for="select_season">Player</label>

            <div class="select-user">
              <b-input-group size="sm" class="mb-2 autocomplete">
                <i class="bi bi-search"></i>
                <b-form-input type="search" placeholder="Search players / coaches" @input="
                  lookUpSearch(
                    $event,
                    'filterdCoachesUsersData',
                    'coachesUsersData',
                  )
                  "></b-form-input>
                <ul class="autocomplete-results" v-if="filterdCoachesUsersData.length > 0">
                  <li v-for="userData in filterdCoachesUsersData" :key="userData.id" class="autocomplete-result">
                    <span @click="
                      addUserTeamToApprovedAccessList(
                        'Users',
                        userData,
                        'selectedDocumentData',
                      )
                      ">
                      {{ userData.name }}</span>
                  </li>
                </ul>
              </b-input-group>
            </div>
          </div>

          <div class="form__item" v-if="selectedDocumentData.approvedAccessList.category === 'coach'">
            <label class="form__label" for="select_season">Coach</label>

            <div class="select-user">
              <b-input-group size="sm" class="mb-2 autocomplete">
                <i class="bi bi-search"></i>
                <b-form-input type="search" placeholder="Search coaches" @update:modelValue="
                  lookUpSearch(
                    $event,
                    'filterdCoachesUsersData',
                    'coachesData',
                  )
                  "></b-form-input>
                <ul class="autocomplete-results" v-if="filterdCoachesUsersData.length > 0">
                  <li v-for="userData in filterdCoachesUsersData" :key="userData.id" class="autocomplete-result">
                    <span @click="
                      addUserTeamToApprovedAccessList(
                        'Users',
                        userData,
                        'selectedDocumentData',
                      )
                      ">
                      {{ userData.name }}</span>
                  </li>
                </ul>
              </b-input-group>
            </div>
          </div>

          <div class="form__item" style="margin-bottom: 1rem"
            v-if="selectedDocumentData.approvedAccessList.category !== 'all'">
            <label v-if="
              selectedDocumentData.approvedAccessList.category !== 'all' &&
              selectedDocumentData.approvedAccessList.category !== 'club' &&
              selectedDocumentData.approvedAccessList.category !== 'admin' &&
              selectedDocumentData.approvedAccessList.category !== 'scout' &&
              selectedDocumentData.approvedAccessList.category !==
              'physioMain'
            " class="form__label" for="select_season">Access</label>

            <div class="access-pool" v-if="
              selectedDocumentData.approvedAccessList.category !== 'all' &&
              selectedDocumentData.approvedAccessList.category !== 'club' &&
              selectedDocumentData.approvedAccessList.category !== 'admin' &&
              selectedDocumentData.approvedAccessList.category !== 'scout' &&
              selectedDocumentData.approvedAccessList.category !==
              'physioMain'
            ">
              <p v-for="accessData in selectedDocumentData.approvedAccessList
                .users" :key="accessData.id" class="has-access" @click="removeAccess(accessData.id, 'Users')">
                {{ accessData.name }} <span>x</span>
              </p>
              <p v-for="accessData in selectedDocumentData.approvedAccessList
                .teams" :key="accessData.id" class="has-access" @click="removeAccess(accessData.id, 'Teams')">
                {{ accessData.teamName }} <span>x</span>
              </p>
            </div>
          </div>
          <p v-if="selectedDocumentData.approvedAccessList.category === 'team'">
            Only the teams selected will have access.
          </p>
          <p v-if="
            selectedDocumentData.approvedAccessList.category === 'club' ||
            selectedDocumentData.approvedAccessList.category === 'admin'
          ">
            This document will be avaiable to everyone within the club.
          </p>
          <p v-if="selectedDocumentData.approvedAccessList.category === 'player'">
            Only the players selected will have access.
          </p>
          <p v-if="selectedDocumentData.approvedAccessList.category === 'coach'">
            Only the coaches selected will have access.
          </p>
          <p v-if="selectedDocumentData.approvedAccessList.category === 'scout'">
            This document will be available to all scouts.
          </p>
          <p v-if="selectedDocumentData.approvedAccessList.category === 'all'">
            If you don't select any access the document will be avaiable to
            everyone.
          </p>
          <p v-if="
            selectedDocumentData.approvedAccessList.category === 'physioMain'
          ">
            This document will only be avaiable to physio, coaches and Admins.
          </p>
        </form>

        <DocumentUploader @getDocumentUploadsCall="getDocumentUploadsCall"
          :documentCategory="selectedDocumentData.approvedAccessList.category" :club_id="userData.clubId"
          :user_id="userData.id" :selectedDocumentData="selectedDocumentData.approvedAccessList" />
      </div>
    </b-modal>

    <b-modal v-if="showCharterModal" v-model="showCharterModal" modal-class="modal-fullscreen" size="xl"
      title="Edit charter" :hide-footer="Boolean(true)">
      <div class="charter-editor">
        <p class="charter-editor__intro">
          <i class="bi bi-info-circle"></i>
          <span>This charter is shown to every player and parent to read and sign. Edit the
            wording below — changes apply to all players in the club.</span>
        </p>

        <section class="editor-card">
          <header class="editor-card__head">
            <div class="editor-card__icon tone-brand"><i class="bi bi-person-check"></i></div>
            <div class="editor-card__title">
              <h5>Players Charter</h5>
              <span>What players agree to</span>
            </div>
          </header>
          <div class="editor-card__body">
            <QuillEditor v-model:content="charterData.player_text" contentType="html" />
          </div>
        </section>

        <section class="editor-card">
          <header class="editor-card__head">
            <div class="editor-card__icon tone-green"><i class="bi bi-people-fill"></i></div>
            <div class="editor-card__title">
              <h5>Parents Charter</h5>
              <span>What parents / guardians agree to</span>
            </div>
          </header>
          <div class="editor-card__body">
            <QuillEditor v-model:content="charterData.parent_text" contentType="html" />
          </div>
        </section>

        <div class="charter-editor__footer">
          <button class="doc-btn doc-btn--ghost" @click="showCharterModal = false">Cancel</button>
          <button class="doc-btn doc-btn--edit" @click="updateCharterText()">
            <i class="bi bi-check-lg"></i> Save charter
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal v-if="showPlayerAssesmentModal" v-model="showPlayerAssesmentModal" size="xl" modal-class="modal-fullscreen"
      title="U11-18 Player Self Assessment" :hide-footer="Boolean(true)">
      <div class="player-cv-content assessment-preview">
        <p class="charter-editor__intro">
          <i class="bi bi-info-circle"></i>
          <span>Reference copy of the U11–18 player self-assessment. Players rate themselves
            across five areas — Technical, Tactical, Physical, Mental and Social.</span>
        </p>
        <div class="multi-radio-wrapper">
          <h4>Technical</h4>
          <p>* Please select all that apply</p>

          <div class="form__item">
            <label class="form__label" for="contact">Can Control the ball with both feet in multiple
              directions:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q1" name="radio-options-1">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Can pass with both feet (short):</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q2" name="radio-options-2">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Has mastered at least one skill move to beat an opponent:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q3" name="radio-options-3">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Can shoot/cross/pass accurately with both feet:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q4" name="radio-options-4">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Can dribble in multiple directions using both feet:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q5" name="radio-options-5">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Can protect the ball from an opponent:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q6" name="radio-options-6">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>
        </div>

        <div class="multi-radio-wrapper">
          <h4>Tactical</h4>
          <p>* Please select all that apply</p>

          <div class="form__item">
            <label class="form__label" for="contact">Makes good decisions on the ball:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q7" name="radio-options-7">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Takes up good positions to receive the ball:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q8" name="radio-options-8">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Makes good runs off the ball at the right times:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q9" name="radio-options-9">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Can play in more than one area of the field:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q10" name="radio-options-10">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Can attack in 1v1 situations:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q11" name="radio-options-11">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Can defend in 1v1 situations:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q12" name="radio-options-12">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Reads situations in the game:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q13" name="radio-options-13">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>
        </div>

        <div class="multi-radio-wrapper">
          <h4>Physical</h4>
          <p>* Please select all that apply</p>

          <div class="form__item">
            <label class="form__label" for="contact">Has good mobility:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q14" name="radio-options-14">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Can Jump and land single leg and double footed:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q15" name="radio-options-15">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Can kick with good technique and generate power (short
              distances):</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q16" name="radio-options-16">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Has good balance (single leg, both sides):</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q17" name="radio-options-17">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Developing basic speed and strength:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q18" name="radio-options-18">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Is flexible and agile:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q19" name="radio-options-19">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>
        </div>

        <div class="multi-radio-wrapper">
          <h4>Mental</h4>
          <p>* Please select all that apply</p>

          <div class="form__item">
            <label class="form__label" for="contact">Has a growth mindset, willing to learn:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q20" name="radio-options-20">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Has a competitive mentality:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q21" name="radio-options-21">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Can build positive relationships and friendships:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q22" name="radio-options-22">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Calmly and directly expresses their and feelings about
              situations:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q23" name="radio-options-23">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Is a problem solver, can find positive solutions to
              challenges:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q24" name="radio-options-24">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Emotional control of winning and losing:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q25" name="radio-options-25">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>
        </div>

        <div class="multi-radio-wrapper">
          <h4>Social</h4>
          <p>* Please select all that apply</p>

          <div class="form__item">
            <label class="form__label" for="contact">Follows Instructions from coaches:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q26" name="radio-options-26">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Respects club and match rules:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q27" name="radio-options-27">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Is a good listener and pays attention to coaches and
              teammates:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q28" name="radio-options-28">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Ignores distractions to focus on tasks:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q29" name="radio-options-29">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Is willing to ask questions to learn:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q30" name="radio-options-30">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Is positive and enjoys being in a group environment with
              others:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q31" name="radio-options-31">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>

          <div class="form__item">
            <label class="form__label" for="contact">Is responsible for their own behaviour:</label>

            <b-form-radio-group id="radio-group-technical" class="custom-control-inline-player-assesment"
              v-model="playerSelfAssessmentU11to18Data.technical.q32" name="radio-options-32">
              <b-form-radio value="5">Excellent</b-form-radio>
              <b-form-radio value="4">Good</b-form-radio>
              <b-form-radio value="3">Progressing Well</b-form-radio>
              <b-form-radio value="2">Requires Improvement</b-form-radio>
              <b-form-radio value="1">Unsatisfactory</b-form-radio>
            </b-form-radio-group>
          </div>
        </div>

        <div class="charter-editor__footer">
          <button class="doc-btn doc-btn--edit" @click="showPlayerAssesmentModal = false">
            <i class="bi bi-check-lg"></i> Done
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal v-if="showJobDescriptionModal" v-model="showJobDescriptionModal" modal-class="modal-fullscreen" size="xl"
      :title="`Edit ${jobDescription.job_type} job description`" :hide-footer="Boolean(true)">
      <div class="charter-editor">
        <p class="charter-editor__intro">
          <i class="bi bi-info-circle"></i>
          <span>This job description is signed by the {{ jobDescription.job_type }}. Edit the
            wording below — changes apply to this role club-wide.</span>
        </p>

        <section class="editor-card">
          <header class="editor-card__head">
            <div class="editor-card__icon tone-blue"><i class="bi bi-clipboard-check"></i></div>
            <div class="editor-card__title">
              <h5>{{ jobDescription.job_type }}</h5>
              <span>Job description</span>
            </div>
          </header>
          <div class="editor-card__body">
            <QuillEditor v-model:content="jobDescription.text" contentType="html" />
          </div>
        </section>

        <div class="charter-editor__footer">
          <button class="doc-btn doc-btn--ghost" @click="showJobDescriptionModal = false">Cancel</button>
          <button class="doc-btn doc-btn--edit" @click="updateJobDescriptionText()">
            <i class="bi bi-check-lg"></i> Save job description
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal v-if="showCodeOfConductModal" v-model="showCodeOfConductModal" modal-class="modal-fullscreen" size="xl"
      title="Edit code of conduct" :hide-footer="Boolean(true)">
      <div class="charter-editor">
        <p class="charter-editor__intro">
          <i class="bi bi-info-circle"></i>
          <span>The code of conduct is signed by all members of staff. Edit the wording
            below — changes apply club-wide.</span>
        </p>

        <section class="editor-card">
          <header class="editor-card__head">
            <div class="editor-card__icon tone-green"><i class="bi bi-shield-check"></i></div>
            <div class="editor-card__title">
              <h5>Code of Conduct</h5>
              <span>Agreed to by all staff</span>
            </div>
          </header>
          <div class="editor-card__body">
            <QuillEditor v-model:content="codeOfConduct.text" contentType="html" />
          </div>
        </section>

        <div class="charter-editor__footer">
          <button class="doc-btn doc-btn--ghost" @click="showCodeOfConductModal = false">Cancel</button>
          <button class="doc-btn doc-btn--edit" @click="updateCodeOfConductText()">
            <i class="bi bi-check-lg"></i> Save code of conduct
          </button>
        </div>
      </div>
    </b-modal>

    <!-- Charter signing status: who has / hasn't signed, across the club -->
    <b-modal v-if="showCharterStatusModal" v-model="showCharterStatusModal" size="xl" hide-footer
      title="Charter signing status">
      <div class="charter-status">
        <div class="charter-status__summary" v-if="charterStatus.summary">
          <div class="stat"><span class="stat-num">{{ charterStatus.summary.total }}</span><span>Players</span></div>
          <div class="stat"><span class="stat-num">{{ charterStatus.summary.both_signed }}</span><span>Fully signed</span></div>
          <div class="stat"><span class="stat-num">{{ charterStatus.summary.player_signed }}</span><span>Player signed</span></div>
          <div class="stat"><span class="stat-num">{{ charterStatus.summary.parent_signed }}</span><span>Parent signed</span></div>
        </div>

        <div class="charter-status__controls">
          <input type="search" v-model="charterStatusSearch" placeholder="Search player…" class="cs-search" />
          <label class="cs-toggle">
            <input type="checkbox" v-model="charterStatusOutstandingOnly" /> Outstanding only
          </label>
        </div>

        <div v-if="charterStatusLoading" class="cs-loading">Loading…</div>
        <table v-else class="simple-table cs-table">
          <thead>
            <tr>
              <th>Player</th><th>Team</th><th>Player</th><th>Parent</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in filteredCharterStatus" :key="p.player_id">
              <td>{{ p.name }}</td>
              <td>{{ p.teamName || '—' }}</td>
              <td>
                <span v-if="p.player_signed" class="cs-yes" v-b-tooltip.hover
                  :title="p.player_signed_at || ''">✓</span>
                <span v-else class="cs-no">✗</span>
              </td>
              <td>
                <span v-if="p.parent_signed" class="cs-yes" v-b-tooltip.hover
                  :title="p.parent_signed_at || ''">✓</span>
                <span v-else class="cs-no">✗</span>
              </td>
            </tr>
            <tr v-if="filteredCharterStatus.length === 0">
              <td colspan="4" class="cs-empty">No players match.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  CHECK_PROFILE_HAS_DATA,
  USER_POPULATE,
  POPULATE_COACHES_USER_DATA,
} from "@/store/actions/user.js";
import { Users } from "@/services/user-service.js";
import { Teams } from "@/services/teams-service.js";
import { Coaches } from "@/services/coaches-service.js";
import { Documents } from "@/services/documents-service.js";
import { TrainingPlan } from "@/services/training-plan-service.js";
import AppTemplate from "../layouts/App";
import SpinnerLoader from "../../components/LoadingSpinning";
import moment from "moment";
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import removeDuplicatesMixin from "@/mixin/removeDuplicates.js";
import DocumentUploader from "../../components/doucmentUploader";
import Axios from "axios";

// QuillEditor configuration handled per component

export default {
  name: "Documents",
  mixins: [removeDuplicatesMixin],
  components: { AppTemplate, SpinnerLoader, DocumentUploader, QuillEditor },
  data() {
    return {
      // Global-tab document library tiles (data-driven; handlers dispatched
      // by openGlobalDoc so the markup stays a simple v-for).
      signedDocuments: [
        { title: "Player & Parent Charter", audience: "Signed by every player & parent", icon: "bi bi-people-fill", tone: "tone-brand", action: "charter", hasStatus: true },
        { title: "Code of Conduct", audience: "Signed by all staff", icon: "bi bi-shield-check", tone: "tone-green", action: "code" },
        { title: "Goalkeeper Coach", audience: "Job description", icon: "bi bi-clipboard-check", tone: "tone-blue", action: "job", jobType: "Goalkeeper Coach" },
        { title: "Head of Academy Analysis", audience: "Job description", icon: "bi bi-clipboard-check", tone: "tone-blue", action: "job", jobType: "Head Of Analyst" },
        { title: "Head of Academy Physiotherapy", audience: "Job description", icon: "bi bi-clipboard-check", tone: "tone-blue", action: "job", jobType: "Head Physio" },
        { title: "Fair Processing Notice", audience: "Data protection · all staff", icon: "bi bi-file-earmark-lock", tone: "tone-amber", action: "fair" },
      ],
      formDocuments: [
        { title: "U11–18 Player Self Assessment", audience: "Player development form", icon: "bi bi-clipboard-data", tone: "tone-purple", action: "assessment" },
      ],
      // Charter signing status (admin tracking view)
      showCharterStatusModal: false,
      charterStatusLoading: false,
      charterStatus: { summary: null, players: [] },
      charterStatusSearch: "",
      charterStatusOutstandingOnly: false,
      playerSelfAssessmentU11to18Data: {
        technical: {
          q1: 0,
          q2: 0,
          q3: 0,
          q4: 0,
          q5: 0,
          q6: 0,
        },
      },
      gridListView: "grid",
      searchQuery: "",
      sortBy: "date-desc",
      showDocumentNewEditModal: false,
      showDocumentViewModal: false,
      isFullscreen: false,
      pdfLoading: false,
      fileTypeFilter: "all",
      showPlayerAssesmentModal: false,
      showCharterModal: false,
      showCodeOfConductModal: false,
      showJobDescriptionModal: false,
      codeOfConduct: {
        text: "",
      },
      jobDescription: {
        job_type: "",
        text: "",
      },
      allJobDescriptionsData: [],
      charterData: {
        player_id: "",
        team_id: "",
        player_text: "",
        parent_text: "",
        created_date: "",
      },
      publicPath: process.env.BASE_URL,
      editMode: false,
      imageFile: null,
      imageToBeRemoved: null,
      imageURL: "",
      image: {
        imageName: "",
        path: "",
        file: null,
      },
      loadingStuff: false,
      trainingPlanHasData: false,
      selectedUserValue: "",
      weekSelectedNumber: "",
      sessionCompleted: false,
      weeklyDate: "",
      newSessionData: {
        userId: "",
        section: "",
        details: "",
        weekSelectedNumber: "",
        name: "",
        introText: "",
        notes: "",
        day: "",
        year: "",
        week: "",
      },
      userHeaderData: {
        name: "",
        userType: "",
      },
      userData: {
        name: "",
        userType: "",
      },
      coachesData: [],
      coachesUsersData: [],
      trainingPlanArciveData: [],
      trainingPlanData: [],
      sessionDataModal: {},
      selectedTab: "club-tab",
      selectedSessionTab: "details",
      documentData: {
        name: "",
        text: "",
        created_date: moment().format("YYYY-MM-DD"),
        updated_date: null,
        created_by: null,
        edited_by: null,
        signature: false,
        approvedAccessList: {
          teams: [],
          users: [],
        },
      },
      filterdCoachesUsersData: [],
      clubTeams: [],
      filterdTeamsData: [],
      usersDocuments: [],
      selectedDocumentData: {
        approvedAccessList: {
          teams: [],
          users: [],
          category: "all",
        },
      },
      uploadedDocuments: [],
    };
  },
  computed: {
    // mix the getters into computed with object spread operator
    ...mapGetters(["getProfile", "getCoachesUsersData", "getWeekNumber"]),
    filteredCharterStatus() {
      const q = this.charterStatusSearch.trim().toLowerCase();
      return (this.charterStatus.players || []).filter((p) => {
        if (this.charterStatusOutstandingOnly && p.player_signed && p.parent_signed) {
          return false;
        }
        return !q || (p.name || "").toLowerCase().includes(q);
      });
    },
    hasActiveFilters() {
      return this.searchQuery.trim() !== '' || this.fileTypeFilter !== 'all';
    },
  },
  created() {
    this.$store.dispatch(CHECK_PROFILE_HAS_DATA).then((response) => {
      if (response === true) {
        this.setUser();
        this.getAllUsersForClubId();
        this.getTeamsByUserId();
        this.getAllCoaches();

        this.loadAllDocuments();
      } else {
        this.setSectionType();
        let clientId = JSON.parse(
          localStorage.getItem("the_w_selectedClientId"),
        );
        this.getUserById(clientId).then((response) => {
          if (response === true) {
            this.selectedUserValue = clientId;
            this.getAllUsersForClubId();
            this.getTeamsByUserId();
            this.getAllCoaches();
            this.loadAllDocuments();
          }
        });
      }
    });
  },
  methods: {
    gridListSwitch(type) {
      this.gridListView = type;
    },
    removeNumberFromStart(documentName) {
      if (!documentName) return '';
      return documentName.substring(documentName.indexOf("_") + 1);
    },
    isPdfFile(url) {
      if (!url) return false;
      return url.toLowerCase().endsWith('.pdf');
    },
    isImageFile(url) {
      if (!url) return false;
      const ext = url.toLowerCase().split('.').pop();
      return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext);
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      if (this.isFullscreen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
    onPdfLoad() {
      this.pdfLoading = false;
    },
    onPdfError() {
      this.pdfLoading = false;
    },
    getDocumentUrl(filename) {
      if (!filename) return '';
      // Serve documents through PHP endpoint to avoid Vue Router interception
      if (window.location.href.indexOf("8080") > -1) {
        // Development: use MAMP server
        return `http://www.localhost:8888/serve-document.php?file=${encodeURIComponent(filename)}`;
      }
      // Production: use relative path to PHP endpoint
      return `/personal-training-BE/serve-document.php?file=${encodeURIComponent(filename)}`;
    },
    playerAssesmentModal() {
      this.showPlayerAssesmentModal = true;
    },
    jobDescriptionModal(jobType) {
      if (this.allJobDescriptionsData.length > 0) {
        const result = this.allJobDescriptionsData.find(
          (job) => job.job_type === jobType,
        );

        console.log(result);
        console.log(jobType);

        this.jobDescription = result;
      } else {
        this.jobDescription.job_type = jobType;
      }

      this.showModal("jobDescriptionModal");
    },
    codeOfConductModal() {
      this.showModal("codeOfConductModal");
    },
    async getCodeOfConductData() {
      const userData = {
        action: "getCodeOfConductData",
        clubId: this.userData.clubId,
      };
      const response = await Documents.find(userData).catch((error) => {
        console.log(error);
      });

      if (response.status === 200) {
        this.codeOfConduct = response.data;
      }
    },
    async getCharterData() {
      const userData = {
        action: "getCharterData",
        player_id: 0,
      };
      const response = await Documents.find(userData).catch((error) => {
        console.log(error);
      });

      if (response.status === 200) {
        this.charterData = response.data;
      }
    },
    charterModal() {
      this.showModal("charterModal");
    },
    openGlobalDoc(doc) {
      switch (doc.action) {
        case "charter": this.charterModal(); break;
        case "code": this.codeOfConductModal(); break;
        case "job": this.jobDescriptionModal(doc.jobType); break;
        case "fair": this.charterModal("parents"); break;
        case "assessment": this.playerAssesmentModal(); break;
      }
    },
    openCharterStatus() {
      this.showCharterStatusModal = true;
      this.loadCharterStatus();
    },
    async loadCharterStatus() {
      this.charterStatusLoading = true;
      try {
        const responce = await Documents.find({
          action: "getCharterStatus",
          clubId: this.userData.clubId,
        });
        if (responce?.data?.success) {
          this.charterStatus = {
            summary: responce.data.summary,
            players: responce.data.players || [],
          };
        }
      } catch (error) {
        console.log(error);
      }
      this.charterStatusLoading = false;
    },
    async updateJobDescriptionText() {
      const userData = {
        action: "updateJobDescriptionText",
        text: this.jobDescription.text,
        job_type: this.jobDescription.job_type,
        clubId: this.userData.clubId,
        created_date: moment().format("YYYY-MM-DD"),
        id: this.jobDescription.id,
      };

      const response = await Documents.find(userData).catch((error) => {
        console.log(error);
      });

      if (response.status === 200) {
        this.showJobDescriptionModal = false;
        this.$toast.info("job Description updated", {
          title: "Saved",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    async updateCodeOfConductText() {
      const userData = {
        action: "updateCodeOfConductText",
        text: this.codeOfConduct.text,
        clubId: this.userData.clubId,
        created_date: moment().format("YYYY-MM-DD"),
        id: this.charterData.id,
      };

      const response = await Documents.find(userData).catch((error) => {
        console.log(error);
      });

      if (response.status === 200) {
        //this.getCodeOfConductData();

        this.showCodeOfConductModal = false;
        this.$toast.info("code of conduct updated", {
          title: "Saved",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    async updateCharterText() {
      const userData = {
        action: "updateCharterText",
        player_id: 0,
        team_id: 0,
        player_text: this.charterData.player_text,
        parent_text: this.charterData.parent_text,
        created_date: moment().format("YYYY-MM-DD"),
        id: this.charterData.id,
      };

      const response = await Documents.find(userData).catch((error) => {
        console.log(error);
      });

      if (response.status === 200) {
        this.getCharterData();

        this.showCharterModal = false;
        this.$toast.info("Charter updated", {
          title: "Saved",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    filterUploadedDocuments(categorySelected) {
      return this.uploadedDocuments.filter(
        (document) => document.category === categorySelected,
      );
    },
    // New methods for search, filter, and sort
    getFilteredDocuments(category) {
      let docs = this.uploadedDocuments.filter(doc => doc.category === category);

      // Apply search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        docs = docs.filter(doc =>
          this.removeNumberFromStart(doc.name).toLowerCase().includes(query)
        );
      }

      // Apply file type filter
      if (this.fileTypeFilter !== 'all') {
        docs = docs.filter(doc => {
          const ext = this.getFileExtension(doc.url || doc.name);
          switch (this.fileTypeFilter) {
            case 'pdf':
              return ext === 'pdf';
            case 'doc':
              return ['doc', 'docx'].includes(ext);
            case 'image':
              return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext);
            default:
              return true;
          }
        });
      }

      // Apply sorting
      docs = [...docs].sort((a, b) => {
        switch (this.sortBy) {
          case 'date-desc':
            return new Date(b.created_date) - new Date(a.created_date);
          case 'date-asc':
            return new Date(a.created_date) - new Date(b.created_date);
          case 'name-asc':
            return this.removeNumberFromStart(a.name).localeCompare(this.removeNumberFromStart(b.name));
          case 'name-desc':
            return this.removeNumberFromStart(b.name).localeCompare(this.removeNumberFromStart(a.name));
          default:
            return 0;
        }
      });

      return docs;
    },
    getFileExtension(filename) {
      if (!filename) return '';
      return filename.split('.').pop().toLowerCase();
    },
    getDocumentCount(category) {
      return this.uploadedDocuments.filter(doc => doc.category === category).length;
    },
    clearSearch() {
      this.searchQuery = "";
    },
    clearAllFilters() {
      this.searchQuery = "";
      this.fileTypeFilter = "all";
    },
    filterDocuments() {
      // Reactive filtering is handled by getFilteredDocuments
    },
    sortDocuments() {
      // Reactive sorting is handled by getFilteredDocuments
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return 'Today';
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      }
    },
    async removeUploadedDocument(id, name) {
      const userData = {
        action: "removeDocumentUpload",
        id: id,
        name: name,
      };
      const response = await Documents.find(userData).catch((error) => {
        console.log(error);
      });

      if (response.status === 200) {
        this.getDocumentUploadsCall();
        this.hideModal("documentEditModal");

        this.$toast.info("Document successfully deleted.", {
          title: "Deleted",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    async sendFile() {
      let API_SERVER = "";
      if (window.location.href.indexOf("8080") > -1) {
        API_SERVER = "http://www.localhost:8888/upload-document.php";
      } else {
        API_SERVER = "/personal-training-BE/upload-document.php";
      }
      const formData = new FormData();
      formData.append("pdf", this.fileUploadData);

      Axios.post(API_SERVER, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
        .then((responce) => {
          // {success:false} comes back with a 200 only for soft rejections;
          // never treat a bare 200 as "stored".
          if (responce.data && responce.data.success === false) {
            throw new Error(responce.data.error || "Upload rejected.");
          }

          this.getDocumentUploadsCall();

          this.$toast.info("Document uploaded.", {
            title: "Uploaded",
            toaster: "b-toaster-bottom-right",
            solid: true,
            appendToast: true,
            variant: "success",
          });
        })
        .catch((error) => {
          const serverError =
            error?.response?.data?.error ||
            (error?.response?.status === 413
              ? "That file is too large to upload."
              : null) ||
            error?.message;

          this.$toast.error(
            serverError || "Failed to upload document. Please try again.",
            {
              title: "Upload Failed",
              toaster: "b-toaster-bottom-right",
              solid: true,
              appendToast: true,
              variant: "danger",
            }
          );
        });
    },
    async getDocumentUploadsCall() {
      this.showDocumentNewEditModal = false;
      const userData = {
        action: "getDocumentUploads",
      };
      const response = await Documents.find(userData).catch((error) => {
        console.log(error);
      });

      if (response.status === 200) {
        this.uploadedDocuments = response.data;
      }
    },
    async getDocumentAccessDataCall(documentId) {
      const userData = {
        document_id: documentId,
        action: "getDocumentAccess",
      };
      const response = await Documents.find(userData).catch((error) => {
        console.log(error);
      });

      if (response.data.length > 0) {
        return response.data;
      } else {
        return null;
      }
    },
    async populateDocumentModelWithAccess(
      documentData,
      documentAccesData,
      objectName,
      modalName,
    ) {
      if (documentAccesData !== null) {
        let accessResponse = documentAccesData.map(async (accessData) => {
          if (accessData.team_id !== null) {
            const teamData = await this.getClubTeamName(accessData.team_id);
            if (teamData) {
              return await this.addUserTeamToApprovedAccessList(
                "Teams",
                teamData,
                objectName,
              );
            }
          }

          if (accessData.user_id !== null) {
            const response = await this.getUserName(accessData.user_id);
            if (response) {
              const useAceessrData = {
                id: accessData.user_id,
                name: response.name,
              };
              return await this.addUserTeamToApprovedAccessList(
                "Users",
                useAceessrData,
                objectName,
              );
            }
          }
        });

        if (accessResponse) {
          this[objectName] = {
            ...documentData,
            approvedAccessList: { ...this[objectName].approvedAccessList },
          };
          this.showModal(modalName);
        }
      }
    },
    async openDocument(documentData) {
      try {
        const documentAccesData = await this.getDocumentAccessDataCall(
          documentData.id,
        );

        if (documentAccesData && documentAccesData.length > 0) {
          this.populateDocumentModelWithAccess(
            documentData,
            documentAccesData,
            "selectedDocumentData",
            "documentViewModal",
          );
        } else {
          // Still show the document even without access data
          this.selectedDocumentData = {
            ...documentData,
            approvedAccessList: {
              teams: [],
              users: [],
              category: "all",
            },
          };
          this.showModal("documentViewModal");
        }
      } catch (error) {
        console.error("Error opening document:", error);
        // Still try to show the document
        this.selectedDocumentData = {
          ...documentData,
          approvedAccessList: {
            teams: [],
            users: [],
            category: "all",
          },
        };
        this.showModal("documentViewModal");
      }
    },
    async editDocument(documentData) {
      const documentAccesData = await this.getDocumentAccessDataCall(
        documentData.id,
      );

      await this.populateDocumentModelWithAccess(
        documentData,
        documentAccesData,
        "selectedDocumentData",
        "documentEditModal",
      );
    },
    getUserName(id) {
      return this.coachesUsersData.find((user) => user.id == id) || null;
    },
    getClubTeamName(id) {
      return this.clubTeams.find((team) => team.id == id) || null;
    },
    async loadAllDocuments() {
      this.getDocumentUploadsCall();
      this.getAllJobDescriptions();
    },
    async getAllJobDescriptions() {
      const data = {
        action: "getAllJobDescriptions",
        clubId: this.userData.clubId,
      };

      const response = await Documents.find(data).catch((error) => {
        console.log(error);
      });

      if (response.data.length > 0) {
        this.allJobDescriptionsData = response.data;
      }
    },
    async addDocumetsAccess() {
      const documentAccessData = {
        approvedAccessList: this.selectedDocumentData.approvedAccessList,
        document_id: this.selectedDocumentData.id,
        action: "addDocumetsAccess",
      };

      const response = await Documents.find(documentAccessData).catch(
        (error) => {
          console.log(error);
        },
      );

      if (response.data.length > 0) {
        this.loadAllDocuments();
      }
    },
    /*async addNewDocumentUploadData() {
      this.loadingStuff = true;

      const userData = {
        ...this.selectedDocumentData,
        action: "addNewDocumentUploadData",
      };
      const response = await Documents.find(userData).catch((error) => {
        console.log(error);
      });
    },*/
    async saveEditedDocument() {
      this.loadingStuff = true;

      this.selectedDocumentData.signature === true ?? 1;
      this.selectedDocumentData.signature === false ?? 0;
      this.selectedDocumentData.updated_date === moment().format("YYYY-MM-DD");

      const userData = {
        ...this.selectedDocumentData,
        action: "saveEditedDocument",
      };
      const response = await Documents.find(userData).catch((error) => {
        console.log(error);
      });

      if (response.data === "Document updated") {
        await this.addDocumetsAccess();
        this.loadingStuff = false;
        this.$toast.info("Document updated successfully.", {
          title: "Updated",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });

        this.loadingStuff = false;
        this.hideModal("documentEditModal");
      }
    },
    async saveNewDocument() {
      this.loadingStuff = true;
      this.documentData.created_by = this.userData.id;

      const userData = {
        ...this.documentData,
        action: "addDocument",
      };
      const response = await Documents.find(userData).catch((error) => {
        console.log(error);
      });

      if (response.data.length > 0) {
        this.hideModal("documentModal");
        this.loadAllDocuments();
        this.loadingStuff = false;
        this.$toast.info("Created Document successfully.", {
          title: "Created",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    async removeAccessCall(accessDataToBeRemoved) {
      this.loadingStuff = true;
      const data = {
        ...accessDataToBeRemoved,
        action: "removeDocumentAccess",
      };
      const response = await Documents.find(data).catch((error) => {
        console.log(error);
      });

      if (response === "access removed") {
        console.log(response);
      }

      const documentAccesData = await this.getDocumentAccessDataCall(
        this.selectedDocumentData.id,
      );
      this.selectedDocumentData.documentAccesData = documentAccesData;
      this.loadingStuff = false;
    },
    removeAccess(id, typeToRemove) {
      let accessToRemove = {
        teams: [],
        users: [],
      };

      if (typeToRemove === "Teams") {
        this.selectedDocumentData.approvedAccessList.teams =
          this.selectedDocumentData.approvedAccessList.teams.filter(
            (team) => team.id !== id,
          );
        accessToRemove.teams.push({
          team_id: id,
          document_id: this.selectedDocumentData.id,
        });
      }
      if (typeToRemove === "Users") {
        this.selectedDocumentData.approvedAccessList.users =
          this.selectedDocumentData.approvedAccessList.users.filter(
            (user) => user.id !== id,
          );
        accessToRemove.users.push({
          user_id: id,
          document_id: this.selectedDocumentData.id,
        });
      }
      if (this.selectedDocumentData.id !== undefined) {
        this.removeAccessCall(accessToRemove);
      }
    },
    addToApprovedAccessList(accessList, newItem) {
      if (!accessList.some((item) => item.id == newItem.id)) {
        accessList.push(newItem);
      }
    },
    addUserTeamToApprovedAccessList(accessType, data, objectName) {
      if (accessType === "Teams") {
        this.addToApprovedAccessList(
          this[objectName].approvedAccessList.teams,
          {
            id: data.id,
            teamName: data.teamName,
          },
        );
        this.filterdTeamsData = [];
        return this[objectName].approvedAccessList.teams.length > 0 ?? true;
      }

      if (accessType === "Users") {
        this.addToApprovedAccessList(
          this[objectName].approvedAccessList.users,
          {
            id: data.id,
            name: data.name,
          },
        );
        this.filterdCoachesUsersData = [];
        return this[objectName].approvedAccessList.users.length > 0 ?? true;
      }
    },
    async getAllCoaches() {
      const userData = {
        action: "getAllCoachesByClubId",
        clubId: this.userData.clubId,
      };

      const responce = await Coaches.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.data.length > 0) {
        this.coachesData = responce.data;
      }
    },
    async getAllUsersForClubId() {
      const userData = {
        clubId: this.userData.clubId,
        action: "getUsersByClubId",
      };
      const response = await Users.find(userData).catch((error) => {
        console.log(error);
      });

      if (response.data.length > 0) {
        this.coachesUsersData = response.data;
      }
    },
    async getTeamsByUserId() {
      const data = {
        action: "getTeamsDataById",
        userId: this.userData.id,
      };

      if (
        this.userData.userType === "Admin" ||
        this.userData.userType === "Coach"
      ) {
        data.clubId = this.userData.clubId;
      }
      const response = await Teams.find(data).catch((error) => {
        console.log(error);
      });
      this.clubTeams = this.removeDuplicates(response.data, "id");
    },
    lookUpSearch(value, filterdData, arrayToFilter) {
      if (value.length > 3) {
        this[filterdData] = [];

        const results = Array.from(value).reduce(
          (a, v, i) => `${a}[^${value.substr(i)}]*?${v}`,
          "",
        );
        const regEx = RegExp(results);

        this[arrayToFilter].filter((v) => {
          if (Object.prototype.hasOwnProperty.call(v, "name")) {
            if (v.name.match(regEx)) {
              this[filterdData].push(v);
            }
          }

          if (Object.prototype.hasOwnProperty.call(v, "teamName")) {
            if (v.teamName.match(regEx)) {
              this[filterdData].push(v);
            }
          }
        });
      } else {
        this[filterdData] = [];
      }
    },
    setSectionType() {
      switch (this.$route.name) {
        case "documents":
          this.newSessionData.section = "Documents";
          break;
      }
    },
    getTrainingTypeAndUserData() {
      if (
        this.userData.userType === "Admin" ||
        this.userData.userType === "Coach"
      ) {
        return {
          trainingType: this.$route.params.trainingPlanType,
          userId: this.$route.params.trainingPlanUserId,
        };
      } else {
        this.selectedUserValue = this.userData.id;
        return {
          trainingType: this.$route.params.trainingPlanType,
          userId: this.userData.id,
        };
      }
    },
    showModal(modelId) {
      switch (modelId) {
        case "documentNewEditModal":
          this.showDocumentNewEditModal = true;
          break;
        case "documentViewModal":
          this.showDocumentViewModal = true;
          break;
        case "playerAssesmentModal":
          this.showPlayerAssesmentModal = true;
          break;
        case "charterModal":
          this.showCharterModal = true;
          break;
        case "codeOfConductModal":
          this.showCodeOfConductModal = true;
          break;
        case "jobDescriptionModal":
          this.showJobDescriptionModal = true;
          break;
        default:
          this.$bvModal.show(modelId);
      }
    },
    hideModal(modelId) {
      switch (modelId) {
        case "documentNewEditModal":
          this.showDocumentNewEditModal = false;
          break;
        case "documentViewModal":
          this.showDocumentViewModal = false;
          this.isFullscreen = false;
          document.body.style.overflow = '';
          break;
        case "playerAssesmentModal":
          this.showPlayerAssesmentModal = false;
          break;
        case "charterModal":
          this.showCharterModal = false;
          break;
        case "codeOfConductModal":
          this.showCodeOfConductModal = false;
          break;
        case "jobDescriptionModal":
          this.showJobDescriptionModal = false;
          break;
        default:
          this.$bvModal.hide(modelId);
      }
    },
    tabChange(tabValue, selectedTabValue) {
      this[tabValue] = selectedTabValue;

      if (selectedTabValue === "global-tab") {
        this.getCharterData();
        this.getCodeOfConductData();
      }
    },
    selectedUseChanged(userId) {
      this.selectedUserValue = userId.selectedUser;
    },
    weekSelectedChanged(number) {
      this.weekSelectedNumber = number.weekSelectedNumber;
    },
    async getUserByIdForHeader(clientId) {
      const userData = {
        clientId: clientId,
        action: "getUserByID",
      };
      const response = await Users.find(userData).catch((error) => {
        console.log(error);
      });

      if (response.data.length > 0) {
        this.userHeaderData = response.data[0];
      }
    },
    async getUserById(clientId) {
      const userData = {
        clientId: clientId,
        action: "getUserByID",
      };
      const response = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (response.data.length > 0) {
        this.userData = response.data[0];
        const userData = response.data[0];
        this.$store.dispatch(USER_POPULATE, { userData });
        if (
          this.userData.userType === "Admin" ||
          this.userData.userType === "Coach"
        ) {
          this.checkForCoachesUsersData();
        }
        return true;
      }
    },
    setUser() {
      if (this.getProfile !== null) {
        this.userData = this.getProfile;
      }
    },
    setHeaderUserData() {
      if (this.getProfile !== null) {
        this.userHeaderData = this.getProfile;
      }
    },
    makeTraingDataObject(actionName) {
      let data = this.newSessionData;
      data.complete = false;
      data.action = actionName;

      if (this.image.file !== null) {
        data.imageFile = this.imageFile;
        data.fileName = this.image.imageName;
      }
      return data;
    },
    actionsAfterFitnessSessionEditCreate() {
      this.hideModal("addEditProgramme");
      this.gettrainingPlansByUserIdAndWeek(this.selectedUserValue);
      this.loadingStuff = false;
      this.$toast.info("Created successfully.", {
        title: "Created",
        toaster: "b-toaster-bottom-right",
        solid: true,
        appendToast: true,
        variant: "success",
      });
    },
    async edittrainingPlan() {
      this.loadingStuff = true;
      const trainingPlanData = this.makeTraingDataObject("edittrainingPlan");
      const response = await TrainingPlan.store(trainingPlanData).catch(
        (error) => {
          console.log(error);
        },
      );

      if (response === "Training session updated") {
        this.actionsAfterFitnessSessionEditCreate();
        this.editMode = false;
      }
    },
    async getAllUsersForThisAdmin() {
      const userData = {
        coachId: this.userData.id,
        action: "getUserByCoachId",
      };
      const response = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (response.data.length > 0) {
        this.coachesUsersData = response.data;
        this.$store.dispatch(POPULATE_COACHES_USER_DATA, this.coachesUsersData);
        localStorage.setItem(
          "coachesUsersData",
          JSON.stringify(this.coachesUsersData),
        );
      }
    },
    checkForCoachesUsersData() {
      if (this.getCoachesUsersData.length > 0) {
        this.coachesUsersData = this.getCoachesUsersData;
      } else {
        if (
          this.userData.userType === "Admin" ||
          this.userData.userType === "Coach"
        ) {
          this.getAllUsersForThisAdmin();
        }
      }
    },
    makeToast(title, message, variant) {
      this.$toast.info(message, {
        title: title,
        variant: variant,
        solid: true,
      });
    },
  },
};
</script>
<style scoped lang="scss">
// Global-tab document library (signed documents + forms)
.doc-library {
  display: flex;
  flex-direction: column;
  gap: 28px;
}
.doc-section__head {
  margin-bottom: 14px;
  h4 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 2px;
    font-size: 1.05rem;
    color: #212529;
    i { color: var(--brand-color, #0d6efd); }
  }
  p { margin: 0; font-size: 0.85rem; color: #6c757d; }
}
.doc-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
}
.doc-tile {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.15s ease, transform 0.15s ease;
  &:hover { box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08); transform: translateY(-1px); }

  &__icon {
    flex-shrink: 0;
    width: 46px;
    height: 46px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    color: #fff;
    &.tone-brand { background: var(--brand-color, #0d6efd); }
    &.tone-green { background: #198754; }
    &.tone-blue  { background: #0d6efd; }
    &.tone-amber { background: #fd7e14; }
    &.tone-purple{ background: #6f42c1; }
  }
  &__body {
    flex: 1;
    min-width: 0;
    h5 { margin: 0 0 2px; font-size: 0.95rem; color: #212529; }
  }
  &__meta { font-size: 0.8rem; color: #6c757d; }
  &__actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }
}
.doc-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 8px;
  padding: 7px 13px;
  font-size: 0.83rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  white-space: nowrap;
  &--edit {
    background: var(--brand-color, #0d6efd);
    color: #fff;
    &:hover { filter: brightness(0.94); }
  }
  &--ghost {
    background: #fff;
    color: #495057;
    border-color: #ced4da;
    &:hover { background: #f8f9fa; }
  }
}
@media (max-width: 520px) {
  .doc-tile { flex-wrap: wrap; }
  .doc-tile__actions { width: 100%; }
}

// Charter editor modal
.charter-editor {
  max-width: 860px;
  margin: 0 auto;

  &__intro {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: #eef4ff;
    border: 1px solid #d6e4ff;
    border-radius: 10px;
    padding: 12px 16px;
    margin-bottom: 22px;
    font-size: 0.88rem;
    color: #33507a;
    i { font-size: 1.1rem; color: var(--brand-color, #0d6efd); margin-top: 1px; }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #e9ecef;
    position: sticky;
    bottom: 0;
    background: #fff;
  }
}
.editor-card {
  border: 1px solid #e9ecef;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: 20px;
  overflow: hidden;

  &__head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
  }
  &__icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    color: #fff;
    &.tone-brand { background: var(--brand-color, #0d6efd); }
    &.tone-green { background: #198754; }
    &.tone-blue  { background: #0d6efd; }
    &.tone-amber { background: #fd7e14; }
    &.tone-purple{ background: #6f42c1; }
  }
  &__title {
    h5 { margin: 0; font-size: 0.98rem; color: #212529; }
    span { font-size: 0.8rem; color: #6c757d; }
  }
  &__body {
    padding: 14px 16px;
    // Give the rich-text editor comfortable room to write in.
    :deep(.ql-container) { min-height: 200px; font-size: 0.95rem; }
    :deep(.ql-editor) { min-height: 200px; }
  }
}

// Player self-assessment (reference preview): each section as a card.
.assessment-preview {
  max-width: 860px;
  margin: 0 auto;

  .multi-radio-wrapper {
    border: 1px solid #e9ecef;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    padding: 0 18px 8px;
    margin-bottom: 20px;

    h4 {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0 -18px 6px;
      padding: 14px 18px;
      font-size: 1rem;
      color: #212529;
      background: #f8f9fa;
      border-bottom: 1px solid #e9ecef;
      border-radius: 12px 12px 0 0;
      border-left: 4px solid var(--brand-color, #0d6efd);
    }
    > p { font-size: 0.8rem; color: #6c757d; margin: 8px 0 4px; }
  }
}

// Charter signing status (admin tracking view)
.charter-status {
  &__summary {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 16px;
    .stat {
      flex: 1 1 120px;
      background: #f8f9fa;
      border-radius: 8px;
      padding: 12px;
      text-align: center;
      display: flex;
      flex-direction: column;
      .stat-num { font-size: 1.6rem; font-weight: 700; color: var(--brand-color, #343a40); }
      span:last-child { font-size: 0.8rem; color: #6c757d; }
    }
  }
  &__controls {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 12px;
    .cs-search { flex: 1; padding: 6px 10px; border: 1px solid #ced4da; border-radius: 6px; }
    .cs-toggle { display: flex; align-items: center; gap: 6px; font-size: 0.85rem; white-space: nowrap;
      input { width: auto; margin: 0; } }
  }
  .cs-table {
    width: 100%;
    th, td { display: table-cell; text-align: left; padding: 8px 10px; }
    .cs-yes { color: #28a745; font-weight: 700; }
    .cs-no { color: #dc3545; font-weight: 700; }
    .cs-empty { text-align: center; color: #6c757d; }
  }
  .cs-loading { padding: 24px; text-align: center; color: #6c757d; }
}

// Modern Document Library Styles

// Ensure Bootstrap Icons always display properly
.bi, :deep(.bi) {
  font-family: "bootstrap-icons" !important;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  display: inline-block;
  vertical-align: middle;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  speak: never;
}

.documents-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  .header-left {
    h1 {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 0 0 8px 0;
      font-size: 28px;
      font-weight: 700;
      color: #1a1a2e;

      i {
        color: #28a745;
      }
    }

    .header-subtitle {
      margin: 0;
      color: #6c757d;
      font-size: 14px;
    }
  }

  .upload-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    font-weight: 500;
    border-radius: 8px;
    transition: all 0.2s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
    }
  }
}

.search-filter-bar {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  .search-box {
    flex: 1;
    min-width: 280px;
    max-width: 500px;
    position: relative;
    display: flex;
    align-items: center;
    background: white;
    border: 2px solid #e9ecef;
    border-radius: 10px;
    padding: 0 16px;
    transition: all 0.2s;

    &:focus-within {
      border-color: #28a745;
      box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
    }

    i {
      color: #6c757d;
      font-size: 16px;
    }

    input {
      flex: 1;
      border: none;
      padding: 12px;
      font-size: 14px;
      background: transparent;
      outline: none;

      &::placeholder {
        color: #adb5bd;
      }
    }

    .clear-search {
      background: none;
      border: none;
      padding: 4px;
      cursor: pointer;
      color: #6c757d;
      display: flex;
      align-items: center;

      &:hover {
        color: #dc3545;
      }
    }
  }

  .filter-controls {
    display: flex;
    gap: 12px;
  }

  .sort-select {
    padding: 12px 16px;
    border: 2px solid #e9ecef;
    border-radius: 10px;
    font-size: 14px;
    background: white;
    cursor: pointer;
    min-width: 160px;

    &:focus {
      outline: none;
      border-color: #28a745;
    }
  }
}

// Filter Panel Styles
.filter-panel {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  border: 1px solid #e9ecef;
  flex-wrap: wrap;

  .filter-label {
    font-size: 14px;
    font-weight: 500;
    color: #6c757d;
    display: flex;
    align-items: center;
    gap: 6px;

    i {
      font-size: 16px;
    }
  }

  .filter-chips {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;

    .filter-chip {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      border: 2px solid #e9ecef;
      border-radius: 20px;
      background: white;
      color: #6c757d;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;

      i {
        font-size: 14px;
      }

      &:hover {
        border-color: #28a745;
        color: #28a745;
      }

      &.active {
        background: linear-gradient(135deg, #28a745 0%, #218838 100%);
        border-color: #28a745;
        color: white;
        box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
      }
    }
  }

  .clear-filters-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: none;
    border-radius: 20px;
    background: #fee2e2;
    color: #dc3545;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-left: auto;

    &:hover {
      background: #fecaca;
    }

    i {
      font-size: 14px;
    }
  }
}

.document-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0;
  margin: 0 0 24px 0;
  list-style: none;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0;

  .tab-item {
    margin-bottom: -2px;
  }

  .tab-link {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    color: #6c757d;
    text-decoration: none;
    border: none;
    border-bottom: 3px solid transparent;
    font-weight: 500;
    font-size: 14px;
    transition: all 0.2s;
    background: transparent;
    border-radius: 8px 8px 0 0;

    i {
      font-size: 16px;
    }

    &:hover {
      color: #28a745;
      background: rgba(40, 167, 69, 0.05);
    }

    &.active {
      color: #28a745;
      border-bottom-color: #28a745;
      background: rgba(40, 167, 69, 0.08);
    }

    .tab-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 22px;
      height: 22px;
      padding: 0 6px;
      background: #e9ecef;
      color: #495057;
      border-radius: 11px;
      font-size: 12px;
      font-weight: 600;
    }

    &.active .tab-badge {
      background: #28a745;
      color: white;
    }
  }
}

.view-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e9ecef;
  width: 100%;

  .results-count {
    color: #6c757d;
    font-size: 14px;
  }

  .view-toggle {
    display: flex;
    gap: 4px;
    background: #f8f9fa;
    padding: 4px;
    border-radius: 8px;

    .view-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border: none;
      background: transparent;
      color: #6c757d;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        color: #28a745;
        background: white;
      }

      &.active {
        background: white;
        color: #28a745;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }
    }
  }
}

// Grid view card
.document-card {
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e9ecef;
  transition: all 0.2s;
  min-width: 220px;
  max-width: 280px;
  flex: 1;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: #28a745;
  }

  .doc-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    background: linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%);
    border-radius: 12px;
    margin-bottom: 16px;

    i {
      font-size: 28px;
      color: white;
    }
  }

  .doc-info {
    flex: 1;

    .doc-name {
      font-size: 15px;
      font-weight: 600;
      color: #1a1a2e;
      margin: 0 0 12px 0;
      line-height: 1.4;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .doc-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: center;

      .meta-item {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        color: #6c757d;

        i {
          font-size: 12px;
        }
      }

      .category-badge {
        background: #e3f2fd;
        color: #1976d2;
        padding: 2px 8px;
        border-radius: 4px;
        font-weight: 500;
        text-transform: capitalize;
      }
    }
  }

  .doc-actions {
    display: flex;
    gap: 8px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e9ecef;

    .action-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 8px;
      border: none;
      cursor: pointer;
      transition: all 0.2s;
      text-decoration: none;

      i {
        font-size: 16px;
      }

      &.view-btn-action {
        background: #e3f2fd;
        color: #1976d2;

        &:hover {
          background: #1976d2;
          color: white;
        }
      }

      &.download-btn {
        background: #e8f5e9;
        color: #388e3c;

        &:hover {
          background: #388e3c;
          color: white;
        }
      }

      &.delete-btn {
        background: #ffebee;
        color: #d32f2f;

        &:hover {
          background: #d32f2f;
          color: white;
        }
      }
    }
  }
}

// List view item
.document-list-item {
  display: flex;
  align-items: center;
  gap: 16px;
  background: white;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  transition: all 0.2s;

  &:hover {
    border-color: #28a745;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .doc-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%);
    border-radius: 10px;
    flex-shrink: 0;

    i {
      font-size: 24px;
      color: white;
    }
  }

  .doc-info {
    flex: 1;
    min-width: 0;

    .doc-name {
      font-size: 15px;
      font-weight: 600;
      color: #1a1a2e;
      margin: 0 0 6px 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .doc-meta {
      display: flex;
      gap: 16px;
      align-items: center;

      .meta-item {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 13px;
        color: #6c757d;

        i {
          font-size: 13px;
        }
      }

      .category-badge {
        background: #e3f2fd;
        color: #1976d2;
        padding: 2px 8px;
        border-radius: 4px;
        font-weight: 500;
        font-size: 12px;
        text-transform: capitalize;
      }
    }
  }

  .doc-actions {
    display: flex;
    gap: 8px;

    .action-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 38px;
      height: 38px;
      border-radius: 8px;
      border: none;
      cursor: pointer;
      transition: all 0.2s;
      text-decoration: none;

      i {
        font-size: 16px;
      }

      &.view-btn-action {
        background: #e3f2fd;
        color: #1976d2;

        &:hover {
          background: #1976d2;
          color: white;
        }
      }

      &.download-btn {
        background: #e8f5e9;
        color: #388e3c;

        &:hover {
          background: #388e3c;
          color: white;
        }
      }

      &.delete-btn {
        background: #ffebee;
        color: #d32f2f;

        &:hover {
          background: #d32f2f;
          color: white;
        }
      }
    }
  }
}

// Modern empty state
.empty-state-modern {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  width: 100%;

  .empty-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-radius: 20px;
    margin-bottom: 24px;

    i {
      font-size: 36px;
      color: #adb5bd;
    }
  }

  h3 {
    margin: 0 0 8px 0;
    font-size: 20px;
    font-weight: 600;
    color: #495057;
  }

  p {
    margin: 0 0 24px 0;
    color: #6c757d;
    font-size: 14px;
  }

  .btn {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

// Grid layout adjustments
.grid-view-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.list-view-wrapper {
  display: block;

  .document-list-item {
    width: 100%;
  }
}

// Enhanced Document Viewer Modal
.document-viewer-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.2s ease;
}

.document-viewer-modal {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: slideUp 0.3s ease;

  &.is-fullscreen {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;

    .viewer-content {
      height: calc(100vh - 60px);
    }

    .pdf-embed {
      height: calc(100vh - 80px);
    }
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px 12px 0 0;

  .viewer-title {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;

    .pdf-icon {
      font-size: 1.5rem;
      color: #dc3545;
    }

    h3 {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 600;
      color: #1f2937;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .viewer-controls {
    display: flex;
    align-items: center;
    gap: 8px;

    .control-btn {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      border: none;
      background: #f3f4f6;
      color: #4b5563;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      text-decoration: none;

      &:hover {
        background: #e5e7eb;
        color: #1f2937;
      }

      &.download {
        background: #dcfce7;
        color: #16a34a;

        &:hover {
          background: #bbf7d0;
          color: #15803d;
        }
      }

      &.close {
        background: #fee2e2;
        color: #dc2626;

        &:hover {
          background: #fecaca;
          color: #b91c1c;
        }
      }

      i {
        font-size: 1.1rem;
      }
    }
  }
}

.viewer-content {
  flex: 1;
  min-height: 0;
  padding: 20px;
  background: #f9fafb;
  overflow: hidden;
}

.pdf-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;

  .loading-spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  p {
    color: #6b7280;
    font-weight: 500;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.pdf-viewer-wrapper {
  width: 100%;
  height: 100%;

  .pdf-embed {
    width: 100%;
    height: 70vh;
    min-height: 500px;
    border: none;
    border-radius: 8px;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
}

.image-viewer-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 400px;

  .image-preview {
    max-width: 100%;
    max-height: 70vh;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
}

.html-content {
  background: white;
  padding: 24px;
  border-radius: 8px;
  max-height: 70vh;
  overflow-y: auto;
}

.file-fallback {
  text-align: center;
  padding: 60px 20px;

  .fallback-icon {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;

    i {
      font-size: 2.5rem;
      color: white;
    }
  }

  h4 {
    margin: 0 0 8px;
    font-size: 1.25rem;
    color: #1f2937;
  }

  p {
    color: #6b7280;
    margin-bottom: 24px;
  }

  .download-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: linear-gradient(135deg, #28a745 0%, #218838 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
    }
  }
}

.viewer-bottom-bar {
  display: none;
  padding: 12px 16px;
  background: white;
  border-top: 1px solid #e5e7eb;
  gap: 12px;
  border-radius: 0 0 12px 12px;

  .action-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    background: #f3f4f6;
    color: #4b5563;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
      background: #e5e7eb;
    }

    &.primary {
      background: linear-gradient(135deg, #28a745 0%, #218838 100%);
      color: white;

      &:hover {
        transform: translateY(-1px);
      }
    }
  }
}

// Responsive adjustments for document viewer
@media (max-width: 768px) {
  .document-viewer-modal-overlay {
    padding: 0;
  }

  .document-viewer-modal {
    max-height: 100vh;
    border-radius: 0;

    &.is-fullscreen {
      .viewer-content {
        height: calc(100vh - 120px);
      }
    }
  }

  .viewer-header {
    border-radius: 0;
    padding: 12px 16px;

    .viewer-title {
      h3 {
        font-size: 0.95rem;
        max-width: 150px;
      }
    }

    .viewer-controls .control-btn {
      width: 36px;
      height: 36px;

      &:not(.close) {
        display: none;
      }
    }
  }

  .viewer-content {
    padding: 12px;
  }

  .pdf-viewer-wrapper .pdf-embed {
    height: 60vh;
    min-height: 300px;
  }

  .viewer-bottom-bar {
    display: flex;
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .documents-header {
    .header-left h1 {
      font-size: 22px;
    }
  }

  .search-filter-bar {
    flex-direction: column;

    .search-box {
      max-width: none;
    }

    .filter-controls {
      width: 100%;
    }

    .sort-select {
      flex: 1;
    }
  }

  .filter-panel {
    padding: 10px 12px;
    gap: 12px;

    .filter-label {
      display: none;
    }

    .filter-chips {
      flex: 1;
      justify-content: center;

      .filter-chip {
        padding: 6px 12px;
        font-size: 12px;

        i {
          font-size: 12px;
        }
      }
    }

    .clear-filters-btn {
      width: 100%;
      justify-content: center;
      padding: 8px;
    }
  }

  .document-tabs {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 8px;

    .tab-link {
      padding: 10px 14px;
      white-space: nowrap;

      span:not(.tab-badge) {
        display: none;
      }
    }
  }

  .document-card {
    max-width: none;
    min-width: 100%;
  }

  .document-list-item {
    flex-wrap: wrap;

    .doc-actions {
      width: 100%;
      justify-content: flex-end;
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid #e9ecef;
    }
  }
}

// Legacy styles below
.simple-table {
  border-collapse: collapse;

  th,
  td {
    padding: 0.5rem;
  }

  tr {
    border-bottom: 1px solid #dddddd;

    .main-button {
      margin-bottom: 0;
    }
  }

  thead tr {
    background-color: #acacac;
    color: #ffffff;
    text-align: left;
  }
}

.list-view-wrapper {
  display: block !important;
}

.grid-view-wrapper {
  display: flex;
  gap: 1rem;
}

.list {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #c3cdd5;

  img {
    width: 2rem;
  }

  h3 {
    margin: 0;
    text-transform: none;
  }

  .doc-buttons {
    margin: 0;
  }
}

.grid-list {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 2rem;
  border-bottom: 1px dashed #c3cdd5;
  width: 100%;

  .btn-outline-secondary {
    border: none !important;
  }
}

.document-uploads-tab {
  padding: 2rem;
}

.signed-documents-wrapper {
  display: block;

  .signed-documents {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }
}

.tabs-wrapper .tab-content {
  padding: 1rem;
}

.sorting-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: start;

  .filter-by {
    background: transparent;
  }

  .search {
    max-width: 20rem;

    input {
      padding: 0.5rem;
      font-size: 0.875rem;
      border-radius: 0;
      background: "white";
    }

    .input-group-text {
      padding: 0.5rem;
      border-radius: 0;
    }
  }
}

.access-pool {
  padding: 1rem;
  border: 1px solid #dddddd;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  .has-access {
    background: #e3f0fb;
    padding: 0.5rem;
    display: block;
    border-radius: 10px;
    cursor: pointer;

    &:hover,
    &:focus {
      background: #f8e0e0;
    }
  }
}

.buttons-row {
  margin-top: 1rem;
  padding-top: 1rem;
}

.file-upload {
  border: 1px solid #dddddd;
  padding: 1rem;
  margin-bottom: 1rem;
}

.documents-tab {
  display: flex;
  flex-wrap: wrap;

  .document {
    max-width: 14rem;
    text-align: center;
    padding: 1rem;
    margin: 1rem;
    border-radius: 4px;
    box-shadow: 0 4px 16px rgba(138, 138, 138, 0.3);
    border: 1px solid transparent;

    &:hover,
    &:focus {
      border: 1px solid $secondary-color;
    }

    h3 {
      margin-top: 1rem;
      font-size: 1rem;
      overflow: hidden;
      white-space: nowrap;
      text-transform: inherit;
    }

    img {
      width: 40%;
    }
  }
}

.doc-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1rem;

  svg {
    border: 1px solid #c3cdd5;
    padding: 0.3rem;
    font-size: 1.7rem;

    &:hover,
    &:focus {
      cursor: pointer;
      background: #eaeff3;
    }
  }
}
</style>
